import * as React from "react";
import { useState } from "react";
import {
  Card,
  CardHeader,
  Text,
  makeStyles,
  shorthands,
  tokens,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Button,
} from "@fluentui/react-components";
import { Info24Regular, CheckmarkCircle24Regular } from "@fluentui/react-icons";
import { ConversationLetter } from "../services/conversationService";

const useStyles = makeStyles({
  card: {
    width: "100%",
    marginBottom: "16px",
  },
  messageBar: {
    marginBottom: "16px",
  },
  successMessageBar: {
    marginBottom: "16px",
    backgroundColor: "#E6F4F4",
    ...shorthands.border("1px", "solid", "#57C1C1"),
  },
  timeline: {
    marginTop: "16px",
    paddingLeft: "0",
  },
  timelineItem: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "16px",
    paddingBottom: "16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    "&:last-child": {
      borderBottom: "none",
      marginBottom: "0",
      paddingBottom: "0",
    },
  },
  timelineDot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: "#0B5D5D",
    marginRight: "12px",
    marginTop: "6px",
    flexShrink: 0,
  },
  timelineContent: {
    flex: 1,
  },
  timelineTitle: {
    fontWeight: 600,
    marginBottom: "4px",
  },
  timelineMeta: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    marginBottom: "2px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  timelineDirectionReceived: {
    fontSize: "11px",
    padding: "3px 8px",
    borderRadius: "4px",
    backgroundColor: "#E3F2FD",
    color: "#1565C0",
    display: "inline-block",
    marginTop: "4px",
    fontWeight: 500,
    border: "1px solid #90CAF9",
  },
  timelineDirectionSent: {
    fontSize: "11px",
    padding: "3px 8px",
    borderRadius: "4px",
    backgroundColor: "#E8F5E9",
    color: "#2E7D32",
    display: "inline-block",
    marginTop: "4px",
    fontWeight: 500,
    border: "1px solid #81C784",
  },
  newConversationIcon: {
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  existingConversationIcon: {
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  loadMoreButton: {
    marginTop: "12px",
    width: "100%",
  },
  promptText: {
    lineHeight: "1.5",
  },
  promptSubtitle: {
    display: "block",
    fontSize: "12px",
    color: "#666",
    marginTop: "2px",
  },
  promptSubtitleSuccess: {
    display: "block",
    fontSize: "12px",
    color: "#0B5D5D",
    marginTop: "2px",
  },
});

interface ConversationPromptProps {
  isNewConversation: boolean;
  conversationLetters: ConversationLetter[];
}

const ConversationPrompt: React.FC<ConversationPromptProps> = ({
  isNewConversation,
  conversationLetters,
}) => {
  const styles = useStyles();
  const [displayLimit, setDisplayLimit] = useState(5); // Show 5 letters initially
  const totalCount = conversationLetters.length;
  const displayedLetters = conversationLetters.slice(0, displayLimit);
  const hasMore = displayLimit < totalCount;

  const getDirectionLabel = (direction: string): string => {
    return direction === "inbound" ? "Received" : "Sent";
  };

  const getDirectionStyle = (direction: string): string => {
    return direction === "inbound" ? styles.timelineDirectionReceived : styles.timelineDirectionSent;
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleLoadMore = () => {
    setDisplayLimit((prev) => prev + 5); // Load 5 more at a time
  };

  if (isNewConversation) {
    // New conversation - first email in thread
    return (
      <Card className={styles.card}>
        <CardHeader
          header={
            <Text weight="bold" size={400} style={{ color: "#0B5D5D" }}>
              Letters from this thread:
            </Text>
          }
        />
      <MessageBar className={styles.messageBar}>
        <MessageBarBody>
          <MessageBarTitle>First message in this conversation</MessageBarTitle>
          <Text className={styles.promptSubtitle}>(No previous messages from this thread are saved yet.)</Text>
        </MessageBarBody>
      </MessageBar>
      </Card>
    );
  }

  // Existing conversation - show timeline of previous letters
  return (
    <Card className={styles.card}>
      <CardHeader
        header={
          <div>
            <Text weight="bold" size={400} style={{ color: "#0B5D5D", display: "block" }}>
              Letters from this thread:
            </Text>
            <Text size={200} style={{ color: "#666", display: "block", marginTop: "4px" }}>
              ({totalCount} {totalCount === 1 ? "letter" : "letters"})
            </Text>
          </div>
        }
      />
      <MessageBar className={styles.successMessageBar}>
        <MessageBarBody>
          <MessageBarTitle style={{ color: "#0B5D5D" }}>Related messages found</MessageBarTitle>
          <Text className={styles.promptSubtitleSuccess}>(Messages from this thread are already saved below.)</Text>
        </MessageBarBody>
      </MessageBar>

      <div className={styles.timeline}>
        {displayedLetters.map((letter) => (
          <div key={letter.letter_id} className={styles.timelineItem}>
            <div className={styles.timelineDot} />
            <div className={styles.timelineContent}>
              <div className={styles.timelineTitle}>{letter.title}</div>
              <div className={styles.timelineMeta}>
                {formatDate(letter.user_letter_date)} · {letter.uploaded_by}
              </div>
              <div className={getDirectionStyle(letter.direction)}>
                {getDirectionLabel(letter.direction)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Load More button if there are more letters */}
      {hasMore && (
        <Button
          appearance="secondary"
          className={styles.loadMoreButton}
          onClick={handleLoadMore}
        >
          Load More ({totalCount - displayLimit} remaining)
        </Button>
      )}
    </Card>
  );
};

export default ConversationPrompt;
